import { Component, inject, OnDestroy, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import {
  LibSubheaderComponent,
  LibHeaderComponent,
  LibFooterComponent,
  BannerConfig,
  BannerComponent,
  MessageCenterService
} from '@k12/app-components';
import { Subject, Subscription, takeUntil } from 'rxjs';
import {
  SystemService,
  LoadingSpinnerService,
  ToastMessageService,
  AuthService,
  NotificationService,
  InactivityMonitorService,
  AlertBannerService,
  AppInsightsService,
  isFullPageRoute,
  isSameAccountToken,
  isWizardRoute,
  normalizeAccountToken,
  registerSharedIcons,
  SIGN_OUT_PATH,
  TokenValidationService
} from '@k12/app-services';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { SchoolsService } from './services/schools.service';
import { School } from './models/school.model';
import { TopMenuData, ToastMessageType, HeaderItem } from '@k12/models';
import { MsalService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '../environments/environment';
import { SchoolAuthService } from './services/school-auth.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterModule,
    LibSubheaderComponent,
    LibHeaderComponent,
    LibFooterComponent,
    MatCardModule,
    MatProgressSpinnerModule,
    BannerComponent
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly ngUnsubscribe: Subject<void> = new Subject();
  private readonly messageCenterService: MessageCenterService = inject(MessageCenterService);
  private readonly schoolAuthService = inject(SchoolAuthService);
  systemService = inject(SystemService);
  dialogService = inject(MatDialog);
  schoolService = inject(SchoolsService);
  matIconRegistry: MatIconRegistry = inject(MatIconRegistry);
  private readonly inactivityMonitor = inject(InactivityMonitorService);
  private userName: string = '';
  private readonly tokenValidationService: TokenValidationService = inject(TokenValidationService);

  domSanitizer = inject(DomSanitizer);
  router = inject(Router);
  private readonly toastMessageService = inject(ToastMessageService);

  private readonly accountId?: string = '';
  private readonly accountToken?: string = '';
  stickyToasties: number[] = [];
  private schools: School[] = [];
  private currentSchoolProgramPartnerId: string = '';

  isProvider: boolean = true;
  title = 'schools';

  public authService: AuthService = inject(AuthService);
  public isHdma: boolean = false;
  public menu: TopMenuData[] = [];
  headerItems: HeaderItem[] = [];
  loading: boolean = false;
  adminUrl: string = environment.adminApiUrl;
  private readonly notificationService = inject(NotificationService);
  private readonly appInsightsService = inject(AppInsightsService);

  public subscriptions: Subscription = new Subscription();

  public currentSchool$ = this.schoolService.getCurrentSchool().pipe(takeUntil(this.ngUnsubscribe));
  public showLoadingSpinner: WritableSignal<boolean> = signal(false);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly alertBannerService = inject(AlertBannerService);
  private readonly msalService = inject(MsalService);
  public bannerConfig: BannerConfig[] = [];

  private readonly destroy$: Subject<void> = new Subject();

  // Routes that bring their own logo header, so both portal blocks are suppressed
  public isFullPageRoute: WritableSignal<boolean> = signal(false);
  // Wizard routes: the portal header stays so sign-out is reachable, the subheader goes
  public isWizardRoute: WritableSignal<boolean> = signal(false);

  constructor() {
    this.accountId = this.msalService.instance.getActiveAccount()?.localAccountId;
    if (this.accountId) {
      this.accountToken = normalizeAccountToken(this.accountId);
    } else {
      // handle the errors
    }
    registerSharedIcons(this.matIconRegistry, this.domSanitizer);

    this.applyChromeForUrl(window.location.pathname);

    // Listen for route changes to decide which chrome this route gets
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.applyChromeForUrl(event.url);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    if (window.location.pathname !== SIGN_OUT_PATH) {
      this.inactivityMonitor.startTracking(60, 5);

      // Internal Subject (inactivity monitor); cannot error by construction, so no error handler is needed.
      this.inactivityMonitor.isIdleWarning.subscribe((res) => {
        if (res) {
          this.toastMessageService.showToast(ToastMessageType.Info, 'Your session is about to expire.');
        }
      });
    }

    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      await this.initHouseholdWithValidation();
    } else {
      // Defer until login completes
      this.msalService.instance.addEventCallback(async (event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS) {
          const acct = this.msalService.instance.getAllAccounts()[0];
          if (acct) {
            this.msalService.instance.setActiveAccount(acct);
            await this.initHouseholdWithValidation();
          }
        }
      });
    }

    this.loadingSpinnerService.loadingSpinner$.pipe(takeUntil(this.destroy$))
      // Internal Subject; cannot error by construction, so no error handler is needed.
      .subscribe((showSpinner: boolean) => {
      this.showLoadingSpinner.set(showSpinner);
    });

    this.buildMenu();

    // SignalR event relay (internal Subject); cannot error by construction, so no error handler is needed.
    this.messageCenterService.unReadUpdate.pipe(takeUntil(this.ngUnsubscribe)).subscribe((res) => {
      const message = JSON.parse(res);
      if (message.body.length == 0) {
        return;
      }
      this.systemService.unreadMessageCount = message.body.length;
    });

    this.notificationService.refreshNotifications.pipe(takeUntil(this.destroy$)).subscribe({
      // Internal Subject trigger; the delegated call (getNotifications) has its own error handling.
      next: () => this.getNotifications()
    });

    // Internal Subject; cannot error by construction, so no error handler is needed.
    this.alertBannerService.bannerConfig.pipe(takeUntil(this.destroy$)).subscribe((configs: BannerConfig[]) => {
      this.bannerConfig = configs;
    });
  }

  public updateUnreadMessageCount(): void {
    const account = this.msalService.instance.getActiveAccount()?.localAccountId || '';
    const token = normalizeAccountToken(account);
    this.messageCenterService.GetAllAccountMessagesAndTopics().subscribe({
      next: (res) => {
        res.forEach((item) => {
          item.unReadCount = item.messages.filter(
            (x) => isSameAccountToken(x.recipientToken, token) && !x.dateRead
          ).length;
        });
        this.systemService.unreadMessageCount = res.filter((x) => x.unReadCount > 0).length ?? 0;
      },
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.ngUnsubscribe.complete();
  }

  // New method that wraps the validation logic
  private async initHouseholdWithValidation(): Promise<void> {
    try {
      // Validate and provision token if needed
      const isValid = await this.tokenValidationService.validateAndProvisionToken(
        environment.adminApiUrl, // or your specific API URL
        'School'
      );

      if (!isValid) {
        console.warn('Token validation failed, but continuing with initialization');
      }

      // Continue with normal initialization
      this.initSchoolsApp(); // Your existing init method
    } catch (error) {
      console.error('Error during token validation:', error);
      // Decide whether to continue or show error
      this.initSchoolsApp(); // Continue anyway, or handle differently
    }
  }

  private initSchoolsApp(): void {
    const account = this.msalService.instance.getActiveAccount();
    this.userName = account?.name || '';

    this.schoolService.init();

    // Subscribe to schools list to update header dynamically
    // Internal Subject; cannot error by construction, so no error handler is needed.
    this.schoolService.schools.pipe(takeUntil(this.destroy$)).subscribe((schools) => {
      this.schools = schools;
      this.buildHeader();
    });

    // Subscribe to current school to update header with checkmark
    this.schoolService
      .getCurrentSchool()
      .pipe(takeUntil(this.destroy$))
      // Internal Subject; cannot error by construction, so no error handler is needed.
      .subscribe((school) => {
        this.currentSchoolProgramPartnerId = school.programPartnerId;
        this.buildHeader();
      });

    this.buildHeader();
    this.buildMenu();
    // MSAL keeps serving the cached ID token, so roles granted or approved since it was issued never
    // reach the menu. One refresh per load re-reads them; a failure leaves the cached menu in place.
    void this.authService.forceRefresh().then(() => this.buildMenu());
    this.getNotifications();
    this.updateUnreadMessageCount();
    this.getAlertBanners();
  }

  buildMenu() {
    const Menu = [] as TopMenuData[];
    this.menu = [];
    Menu.push({
      icon: 'assets/images/home.svg',
      label: 'Home',
      route: 'home',
      isHome: true,
      cssClass: 'home-menu'
    });
    // Subscribe to currentSchool$ to get the current school name
    // this.currentSchool$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((currentSchool) => {
    //   // Find and update the home menu item if it exists
    //   const homeMenuIndex = Menu.findIndex((item) => item.route === 'home');

    //   if (homeMenuIndex !== -1) {
    //     // Update the existing home menu item
    //     const truncatePipe = new TruncatePipe();
    //     Menu[homeMenuIndex].label = truncatePipe.transform(currentSchool.name, 25, true, '...');
    //     Menu[homeMenuIndex].title = currentSchool.name;
    //   } else {
    //     return;
    //   }

    //   // Update the menu array
    //   this.menu = [...Menu];
    // });
    Menu.push({
      icon: 'assets/images/school.svg',
      label: 'My School',
      route: 'mypage',
      isHome: false
    });
    Menu.push({
      icon: 'assets/images/documents.svg',
      label: 'My Documents',
      route: 'documents',
      isHome: false
    });
    if (this.schoolAuthService.canAccessRosters) {
      Menu.push({
        icon: 'assets/images/clipboard-list.svg',
        label: 'Rosters',
        route: 'rosters',
        isHome: false
      });
    }
    Menu.push({
      icon: 'assets/images/chart-simple.svg',
      label: 'Reports',
      route: 'reports',
      dropdown: [
        { label: 'Pending Awards', route: 'reports/pending-awards' },
        { label: 'New Student Applications', route: 'reports/new-student-applications' },
        { label: 'Refunds Due', route: 'reports/refunds-due' },
        { label: 'Award Payment Student Detail', route: 'reports/award-payment-student-detail' },
        { label: 'End of Year Payment Summary', route: 'reports/end-of-year-payment-summary' }
      ],
      isHome: false
    });

    this.menu = Menu;
  }

  buildHeader() {
    // Build profile dropdown with user email, schools list, and logout
    const profileDropdown = [
      // Add all schools to the dropdown with checkmark for selected school
      ...this.schools.map((school) => ({
        label: school.programPartnerId === this.currentSchoolProgramPartnerId ? `✓ ${school.name}` : school.name,
        route: `select-school/${school.programPartnerId}`,
        data: { programPartnerId: school.programPartnerId }
      })),
      {
        label: this.userName,
        route: 'mypage'
      },
      {
        label: 'Logout',
        cmd: true
      }
    ];

    this.headerItems = [
      {
        label: 'FAQ',
        icon: '../assets/images/faq.svg',
        route: 'https://k12.ncseaa.edu/school-admins/',
        url: 'true'
      },
      {
        label: 'Alerts',
        icon: '../assets/images/alerts.svg',
        route: ''
      },
      {
        label: 'Messaging',
        icon: '../assets/images/messages.svg',
        route: 'messages'
      },
      {
        label: 'Profile',
        icon: '../assets/images/profile.svg',
        route: '',
        tooltip: this.userName,
        dropdown: profileDropdown
      }
    ];
  }

  private getNotifications(): void {
    this.notificationService.getNotifications(this.adminUrl).subscribe({
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  private getAlertBanners(): void {
    this.alertBannerService.getAlertBanners(environment.adminApiUrl);
  }

  public dismissBanner(banner: BannerConfig): void {
    this.alertBannerService.dismissBanner(banner);
  }

  logout(): void {
    this.systemService.logout();
  }

  selectSchool(programPartnerId: string): void {
    this.schoolService.setCurrentSchool(programPartnerId);
    this.router.navigate(['mypage']);
  }

  private applyChromeForUrl(url: string): void {
    this.isFullPageRoute.set(isFullPageRoute(url));
    this.isWizardRoute.set(isWizardRoute(url));
  }
}
